import React from 'react';
import Image from 'next/image';
import { RAY_FAN, type Ray } from './convergence-rays';

/**
 * The hero convergence graphic.
 *
 * BRAND-SPEC.md calls this "the one piece worth lifting near-verbatim; keep it
 * a single self-contained component with the ray parameters as props/config".
 * Everything it needs, including its keyframes and its reduced-motion rules,
 * lives in this file's own <style> block. Nothing is added to globals.css: the
 * shell owns the token layer, this component owns its own motion, and the two
 * never have to be edited together.
 *
 * Inline <style> rather than a CSS module because the repo has no *.module.css
 * anywhere, and packages/shared/src/brand/paxrent-logo-animated.tsx already
 * establishes this exact shape for a self-contained animated brand element.
 * next.config.ts sets style-src 'self' 'unsafe-inline', so both the <style>
 * element and the per-ray style attributes are permitted.
 *
 * FOUR layers, not the three the ticket lists. The design reference
 * (docs/revamp-july-2026/handoff/Focal Point Site.dc.html line 45) carries an
 * ink blue ambient wash between the plate and the fan, and it is what stops the
 * greyscale plate reading as a grey smudge. It ships.
 *
 * GEOMETRY. The reference positions all seventeen elements with hand-computed
 * `right: Npx; margin-top: -Mpx` pairs against a fixed 640px desktop canvas.
 * That does not survive being made responsive. Instead everything hangs off a
 * zero-size origin div placed at the convergence point, so scaling the whole
 * composition is one variable (--fp-scale) rather than seventeen recomputed
 * offsets, and each ray's pivot is exact by construction.
 *
 * COLOUR. Full-opacity sand reads the --sand token. The ink blue appears only
 * as alpha variants (0.24 to 0.72) and is written as literal rgba(35,75,122,a),
 * exactly as the reference does, because a hex token cannot carry a variable
 * alpha without color-mix() or relative colour syntax. Those would trade a
 * verbatim port for a browser-support surface on the site's signature element.
 * The literals are the same #234B7A that --ink-blue holds; if that token ever
 * changes, these change with it.
 *
 * The convergence point itself is --fp-right (from the hero's right edge) and
 * --fp-top (from its top). The fan's left-to-right mask and the ambient wash
 * are both expressed relative to it via calc(), so they stay locked to the dot
 * at any viewport width instead of drifting toward the edge.
 *
 * DESKTOP ONLY since #7943. Nothing renders below 768px; see the note above
 * .fp-root. HeroSection's mobile bottom padding was the room this used to
 * occupy and it went with it.
 */

interface ConvergenceFieldProps {
  /**
   * The ray fan. Defaults to RAY_FAN so the array is usable as a module
   * constant; passing it explicitly is the tuning escape hatch.
   */
  rays?: readonly Ray[];
}

/** CSS custom properties are not in React's CSSProperties type. */
type RayStyle = React.CSSProperties & Record<`--fp-${string}`, string | number>;

const FP_CSS = `
/* DESKTOP ONLY, from 768px up.

   #7920 shipped a base tier for phones that moved the convergence point to
   calc(100% - 190px), below the copy rather than behind it, because the fan
   cannot be right-anchored beside a full-width copy column. HeroSection
   reserved 300px of bottom padding for it. What that produced on a phone was a
   grey rectangle sitting under the CTAs with nothing converging on anything,
   costing most of a second screen of scroll (#7943).

   Nothing survives below 768px: not the plate, not the fan, not the dot. The
   dot on its own is a 7px blue circle with a cream halo and no rays entering
   it, which is not a quiet mark, it is a smudge the reader has to explain to
   themselves. The graphic means "many lines resolving to one point" or it means
   nothing, and there is no width under 768 where the fan fits beside copy. The
   hero keeps its type, its colour and its CTAs, which is what a phone reader is
   there for.

   display: none on the root and not a conditional render, because the viewport
   is not knowable at server-render time and a JS gate would either mismatch on
   hydration or flash the graphic before removing it. The markup cost is ~1KB.
   The plate's bytes are handled separately: see the sizes attribute below. */
.fp-root {
  position: absolute;
  inset: 0;
  pointer-events: none;
  display: none;

  --fp-scale: 0.62;
  --fp-blur-scale: 0.85;
  --fp-right: 12%;
  --fp-top: 46%;
}

@media (min-width: 768px) {
  .fp-root { display: block; }
}
/* Blur rides a slower curve than the geometry. Scaling it linearly turns the
   outer rays into hard 1px lines on a smaller canvas, which loses the depth the
   fan depends on: 0.62 of the geometry carries 0.85 of the blur. */
@media (min-width: 1024px) {
  .fp-root { --fp-scale: 0.78; --fp-blur-scale: 0.92; --fp-right: 14%; }
}
@media (min-width: 1280px) {
  .fp-root { --fp-scale: 1; --fp-blur-scale: 1; }
}
/* Past the 1360px content column, track the grid rather than the viewport
   edge. A literal right: 190px drifts the dot further outside the column the
   wider the window gets; this evaluates to exactly 190px at W=1360, which is
   the canvas the design was drawn at. */
@media (min-width: 1360px) {
  .fp-root { --fp-right: calc((100% - 1360px) / 2 + 190px); }
}

/* Layer 1: photographic plate, centred on the convergence point. The blend
   treatment lives on this wrapper, never on the <img>, because next/image
   writes its own inline styles onto the image element.

   Centred by offsetting half its size rather than by translate(-50%), for the
   same reason as .fp-dot below. This one is not animated, so it is not in the
   #7957 bug class and it measured correct through a live resize in both
   Chromium and WebKit. It is converted anyway: it is the largest layer in the
   composition, so a stale resolution here would be a 100px displacement of the
   doorway rather than the glow's 33px, and leaving one percentage-centred
   layer behind would mean the field is only mostly immune. Now nothing in the
   graphic centres itself with a transform. */
.fp-plate {
  position: absolute;
  right: calc(var(--fp-right) - 470px * var(--fp-scale));
  top: calc(var(--fp-top) - 470px * var(--fp-scale));
  width: calc(940px * var(--fp-scale));
  height: calc(940px * var(--fp-scale));
  opacity: 0.5;
  filter: grayscale(0.55) contrast(0.92);
  mix-blend-mode: multiply;
  -webkit-mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 8%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 68%);
          mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 8%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0) 68%);
}

/* Layer 2: ink blue ambient wash. Offset 184px left of the dot at the desktop
   scale, exactly as the reference places it, so it warms the middle of the
   composition rather than haloing the core. */
.fp-wash {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 60% 70% at calc(100% - var(--fp-right) - 184px * var(--fp-scale)) var(--fp-top),
    rgba(35,75,122,0.10) 0%,
    rgba(35,75,122,0) 68%
  );
}

/* Layer 3: the fan. Masked left to right so every outer ray end falls to zero.
   Stop positions are measured from the convergence point (610px / 434px /
   146px to its left at the desktop scale), which reproduces the reference's
   800px-wide container mask at W=1360 and keeps the fade locked to the dot at
   every other width. */
.fp-fan {
  position: absolute;
  inset: 0;
  -webkit-mask-image: linear-gradient(to right,
    rgba(0,0,0,0)    calc(100% - var(--fp-right) - 610px * var(--fp-scale)),
    rgba(0,0,0,0.35) calc(100% - var(--fp-right) - 434px * var(--fp-scale)),
    rgba(0,0,0,1)    calc(100% - var(--fp-right) - 146px * var(--fp-scale)));
          mask-image: linear-gradient(to right,
    rgba(0,0,0,0)    calc(100% - var(--fp-right) - 610px * var(--fp-scale)),
    rgba(0,0,0,0.35) calc(100% - var(--fp-right) - 434px * var(--fp-scale)),
    rgba(0,0,0,1)    calc(100% - var(--fp-right) - 146px * var(--fp-scale)));
}

/* Zero-size anchor at the convergence point. Everything below positions
   against it, so a ray with right: 0 and transform-origin: right center pivots
   exactly on the dot. */
.fp-origin {
  position: absolute;
  right: var(--fp-right);
  top: var(--fp-top);
  width: 0;
  height: 0;
}

.fp-ray {
  position: absolute;
  right: 0;
  top: -1px;
  height: 2px;
  width: calc(var(--fp-len) * 1px * var(--fp-scale));
  background-image: linear-gradient(90deg,
    rgba(35,75,122,0) 0%,
    rgba(35,75,122,0) 6%,
    rgba(35,75,122,var(--fp-alpha)) 48%,
    rgba(35,75,122,var(--fp-alpha)) 64%,
    rgba(35,75,122,0) 100%);
  background-size: var(--fp-band) 100%;
  background-repeat: no-repeat;
  transform-origin: right center;
  transform: rotate(var(--fp-angle));
  filter: blur(calc(var(--fp-blur) * 1px * var(--fp-blur-scale)));
  /* Both keyframe tracks open at opacity 0. Matching that here keeps a
     delayed ray from flashing at full alpha before its delay elapses. */
  opacity: 0;
  animation-duration: var(--fp-dur);
  animation-timing-function: var(--fp-ease);
  animation-delay: var(--fp-delay);
  animation-iteration-count: infinite;
}
.fp-flow-a { animation-name: fpFlowA; }
.fp-flow-b { animation-name: fpFlowB; }

/* Tier ladder. The gates stay keyed to each ray's declared tier so the tier
   field in convergence-rays.ts keeps its literal meaning, but because the root
   itself does not render below 768px the ladder now has two visible steps
   rather than four: 7 rays at 768px (base + sm + md) and 11 at 1280px. The
   640px gate is what would light up first if the field ever returned to small
   screens; it costs one rule.

   All eleven are rendered server side and gated by CSS. Choosing the count in
   JS would need the viewport at render time, which a server render does not
   have, so the options there are a hydration mismatch or a visible flash of
   eleven rays before settling on seven. display: none generates no box, so the
   hidden rays cost zero paint and their animations do not run: this is a real
   reduction in animated blurred elements on the devices that cannot afford
   them, not a visibility trick. The price is about 900 bytes of markup.

   content-visibility is deliberately not used. It only skips content outside
   the viewport, and the hero is above the fold by definition. */
.fp-t-sm, .fp-t-md, .fp-t-xl { display: none; }
@media (min-width: 640px)  { .fp-t-sm { display: block; } }
@media (min-width: 768px)  { .fp-t-md { display: block; } }
@media (min-width: 1280px) { .fp-t-xl { display: block; } }

/* Layer 4: focal point. Six concentric circles of different diameters sharing
   the origin's centre.

   ONE rule owns the centring, and it does it with layout rather than with a
   transform. Each element declares only its diameter in --fp-size; the shared
   rule derives the width, the height and the offsets that put its centre on the
   origin. Concentric by construction: there is no per-element number to get
   wrong, and adding a seventh ring is one --fp-size away.

   NO PERCENTAGE TRANSFORM. The obvious way to centre these is right: 0 plus
   translate(50%, -50%), which is what #7920 shipped and what #7943 left in
   place. It is correct in Chrome and it drifts in Safari, which is the live
   resize bug in #7957: WebKit resolves a percentage translate inside running
   keyframes against the box size captured when the animation was committed, and
   a later layout change does not re-resolve it. Every circle here both animates
   AND changes size with --fp-scale at 768/1024/1280, so crossing a breakpoint
   left each one translating by a stale number of pixels. The displacement is
   half the size delta, so it scaled with the element: on 1440 -> 1200 the 300px
   glow sat 33px up and to the right of a core that had moved 2px. That is
   exactly "the glow is misplaced and the dot is not", and reloading fixed it
   because a fresh animation re-resolves the percentage.

   Offsets in px cannot go stale: they are layout, recomputed on every resize
   like any other length, and the keyframes are left carrying scale() alone,
   which is centre-origin by default and needs no box measurement at all. This
   is engine-independent rather than a Safari workaround.

   #7943's fix is still here and still needed, it was just a different bug: the
   reference places the glow at right: 122px with width 300, putting its centre
   81px left of the core's. That was a static offset in the source. This one was
   a dynamic offset the source never contained. */
.fp-dot {
  position: absolute;
  width: calc(var(--fp-size) * 1px * var(--fp-scale));
  height: calc(var(--fp-size) * 1px * var(--fp-scale));
  right: calc(var(--fp-size) * 1px * var(--fp-scale) / -2);
  top: calc(var(--fp-size) * 1px * var(--fp-scale) / -2);
  border-radius: 50%;
}

.fp-glow {
  --fp-size: 300;
  background: radial-gradient(circle,
    rgba(245,243,238,0.82) 0%,
    rgba(245,243,238,0.3) 46%,
    rgba(245,243,238,0) 72%);
  animation: fpGlow 7.5s ease-in-out 1s infinite;
}

.fp-ping {
  opacity: 0;
  animation: fpPing 7.5s cubic-bezier(.2,.6,.3,1) infinite;
}
/* All three pings render whenever the field does. #7920 held the outermost one
   back below 768px because three concentric rings on a 42%-scale canvas
   overlapped into one thick smudge; that canvas no longer exists. */
.fp-ping-a {
  --fp-size: 170;
  border: 1px solid rgba(35,75,122,0.3);
  animation-delay: 0s;
}
.fp-ping-b {
  --fp-size: 130;
  border: 1px solid rgba(35,75,122,0.34);
  animation-delay: 1.1s;
}
.fp-ping-c {
  --fp-size: 90;
  border: 1px solid rgba(35,75,122,0.4);
  animation-delay: 2.2s;
}

.fp-ring {
  --fp-size: 38;
  border: 1px solid rgba(35,75,122,0.3);
  animation: fpGlow 7.5s ease-in-out 1s infinite;
}

.fp-core {
  --fp-size: 18;
  background: var(--ink-blue);
  box-shadow: 0 0 calc(26px * var(--fp-scale)) calc(8px * var(--fp-scale)) rgba(35,75,122,0.24);
  animation: fpGlow 7.5s ease-in-out 1s infinite;
}

/* Scrim, two strategies.

   The reference's "leftmost 60%, opaque to 54%" is drawn for a single 1360px
   canvas and it does not generalise, because the copy column is a fixed pixel
   width while the convergence point tracks the viewport. What actually keeps a
   ray off a glyph is the COMBINED alpha at the copy's right edge: the fan's own
   left-to-right mask times whatever the scrim leaves through.

   Measured at that edge (mask alpha x (1 - scrim alpha), before the ray's own
   0.39-0.72):

     1440px   copy ends at 752, dot at 1210   mask 0.30, scrim 0.28  net 0.22
     1024px   copy ends at 632, dot at  880   mask 0.61, scrim 0     net 0.61

   0.22 is the value the reference produces and it is visibly clean. 0.61 is
   not: at 1024 the reference proportion puts ray tips on the headline's last
   glyphs. The gap between the copy and the dot is 458px at 1360 and above but
   only 248px at 1024, so the scrim has to be proportionally wider there.

   #7920 also carried a vertical variant below 768px, opaque behind the copy
   block and clearing over the graphic band at the bottom. That band is gone
   with the rest of the mobile composition, and a scrim over nothing is just an
   unexplained --sand wash over the hero's --sand-3 field, so the variant went
   with it. The scrim now starts at the derived 768px form.

   From 1360px up the reference values are restored verbatim, which does mean a
   step at that breakpoint: the fan reaches further left above it than below.
   That is deliberate. The alternative is carrying the tighter scrim upward and
   shortening the signature desktop fan, and BRAND-SPEC.md is explicit that the
   1360px composition is the one piece worth lifting near-verbatim. A
   discontinuity nobody experiences as motion is the cheaper of the two.

   The derived tiers use a four-stop falloff rather than the reference's single
   linear ramp. A wider scrim has to start fading where the plate begins, and a
   linear ramp starting there is visible as a Mach band: a faint vertical seam
   down the hero. The extra stops approximate an ease and the seam goes away.
   Alpha at the copy's right edge stays above what the measurement above
   requires: 0.75 at 768px, 0.73 at 1024px, 0.88 at 1280px. */
.fp-scrim {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 84%;
  background: linear-gradient(to right,
    var(--sand) 0%,
    var(--sand) 52%,
    rgba(245,243,238,0.86) 70%,
    rgba(245,243,238,0.45) 86%,
    rgba(245,243,238,0) 100%);
}
@media (min-width: 1024px) {
  .fp-scrim { width: 82%; }
}
@media (min-width: 1360px) {
  .fp-scrim {
    width: 60%;
    background: linear-gradient(to right,
      var(--sand) 0%,
      var(--sand) 54%,
      rgba(245,243,238,0) 100%);
  }
}

@keyframes fpFlowA {
  0%   { background-position: -80% 0; opacity: 0; }
  12%  { opacity: 0.45; }
  34%  { opacity: 1; }
  58%  { background-position: 70% 0; opacity: 0.85; }
  80%  { opacity: 0.35; }
  100% { background-position: 175% 0; opacity: 0; }
}
@keyframes fpFlowB {
  0%   { background-position: -80% 0; opacity: 0; }
  20%  { opacity: 0.9; }
  40%  { opacity: 0.5; }
  66%  { opacity: 1; }
  88%  { opacity: 0.2; }
  100% { background-position: 175% 0; opacity: 0; }
}
/* scale() only, no translate. .fp-dot has already put each circle's centre on
   the origin using layout, and scale's default transform-origin is that same
   centre, so these grow and shrink in place with nothing to re-resolve on a
   resize. See the .fp-dot comment for the Safari bug this avoids. */
@keyframes fpGlow {
  0%, 100% { opacity: 0.75; transform: scale(1); }
  50%      { opacity: 1;    transform: scale(1.06); }
}
@keyframes fpPing {
  0%   { opacity: 0.5;  transform: scale(0.6); }
  65%  { opacity: 0.08; transform: scale(1.35); }
  100% { opacity: 0;    transform: scale(1.6); }
}

/* Reduced motion.

   BRAND-SPEC.md asks for the rays held at full opacity with the animation
   dropped. Taken literally that is animation: none, and it does not work: each
   ray's background-position falls back to 0 0, and with background-size 58-73%
   the visible band lands roughly 16-21% along the ray from its OUTER end. Every
   streak clusters at the far edge of the fan and nothing is dense near the dot,
   so the still image reads as a ring of dashes around an unrelated blue point
   rather than as a convergence.

   The fix keeps both halves of the instruction (full opacity, no animation) and
   restores the meaning: a full-length band, transparent at the outer end and at
   full alpha AT the convergence point.

   This is also why globals.css deliberately does not carry the conventional
   blanket "animation-duration: 0.01ms !important" reduce rule. Both flow tracks
   end at opacity 0, so the blanket form would erase the fan instead of holding
   it.

   All media query, no matchMedia and no useReducedMotion hook: a JS read of the
   preference is a hydration mismatch on a server-rendered page. */
@media (prefers-reduced-motion: reduce) {
  .fp-ray {
    animation: none;
    opacity: 1;
    background-size: 100% 100%;
    background-position: 0 0;
    background-image: linear-gradient(90deg,
      rgba(35,75,122,0) 0%,
      rgba(35,75,122,0) 10%,
      rgba(35,75,122,var(--fp-alpha)) 66%,
      rgba(35,75,122,var(--fp-alpha)) 100%);
  }
  .fp-glow { animation: none; opacity: 0.85; }
  /* fpPing opens at scale(0.6) opacity(0.5); with animation: none the rings
     would sit at scale(1) opacity(1), larger and heavier than anything the
     animation ever draws. This is where the animation spends most of its time. */
  .fp-ping {
    animation: none;
    opacity: 0.42;
    transform: scale(0.82);
  }
  .fp-ring,
  .fp-core { animation: none; opacity: 1; }
}
`;

export default function ConvergenceField({ rays = RAY_FAN }: ConvergenceFieldProps) {
  return (
    <div className="fp-root" aria-hidden>
      <style>{FP_CSS}</style>

      {/* No explicit quality prop: next.config.ts declares no images.qualities
          list, so under Next 16 any value other than the default 75 is dropped
          on the floor. 75 is ample for a plate rendered at opacity 0.5 under
          grayscale(0.55). alt="" plus the aria-hidden root, because the plate
          is decorative and the headline carries the meaning.

          The 1px branch under 768px is the point of this attribute now. priority
          emits <link rel="preload" imagesrcset imagesizes>, and the preload
          scanner resolves it against the viewport without knowing the element is
          display: none, so a phone would otherwise fetch a 395px plate it never
          paints. Declaring 1px there makes it pick the smallest candidate Next
          generates instead. The remaining widths are 940 x --fp-scale at each
          tier: 583 at 0.62, 733 at 0.78, 940 at 1. */}
      <div className="fp-plate">
        <Image
          src="/assets/hero-plate-940-v2.jpg"
          alt=""
          fill
          priority
          sizes="(max-width: 767px) 1px, (max-width: 1023px) 583px, (max-width: 1279px) 733px, 940px"
          style={{ objectFit: 'cover' }}
        />
      </div>

      <div className="fp-wash" />

      <div className="fp-fan">
        <div className="fp-origin">
          {rays.map((ray) => (
            <span
              key={ray.angle}
              className={`fp-ray fp-t-${ray.tier} fp-flow-${ray.flow}`}
              style={
                {
                  '--fp-angle': `${ray.angle}deg`,
                  '--fp-len': ray.length,
                  '--fp-alpha': ray.alpha,
                  '--fp-blur': ray.blur,
                  '--fp-band': `${ray.band}%`,
                  '--fp-dur': `${ray.duration}s`,
                  '--fp-delay': `${ray.delay}s`,
                  '--fp-ease': ray.easing,
                } as RayStyle
              }
            />
          ))}

          {/* fp-dot carries the centring for all six. Each companion class
              supplies only --fp-size and its own paint. */}
          <span className="fp-dot fp-glow" />
          <span className="fp-dot fp-ping fp-ping-a" />
          <span className="fp-dot fp-ping fp-ping-b" />
          <span className="fp-dot fp-ping fp-ping-c" />
          <span className="fp-dot fp-ring" />
          <span className="fp-dot fp-core" />
        </div>
      </div>

      <div className="fp-scrim" />
    </div>
  );
}
